import {Turnstile, TurnstileInstance} from '@marsidev/react-turnstile';
import clsx from 'clsx';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useLocation, useSearchParams} from 'react-router-dom';
import {Box} from '@mui/material';
import parseJwt from '@lightricks/fortress-web-client/dist/helpers/jwt-parser';
import {
  Banner,
  Body,
  Headline,
  Label,
  designSystemToken,
} from '@lightricks/react-design-system';
import {ButtonProps} from '@lightricks/react-design-system/dist/components/button/ButtonProps';
import AnalyticsService from '@/services/analytics/AnalyticsService';
import {
  FORTRESS_FLOWS,
  VerifyOTPAndSaveTokenCookiesResponse,
} from '@/services/fortress/Fortress';
import Sentry from '@/services/sentry/Sentry';
import {
  flowNames,
  reasons,
  resultFlows,
  screenNames,
  providers,
} from '@/lib/delta/deltaConstants.js';
import signupPixelEvents from '@/lib/marketing-events/signupEvents';
import translate from '@/utils/translate';
import {getEnv} from '@/config/environment';
import {ROUTES} from '@/config/routesConstants';
import {AuthMode} from '@/types/AuthModes';
import {Product} from '@/types/product';
import authenticationUpdaters from '@/api/updaters/authentication';
import AuthForm from '@/components/auth-form';
import Link from '@/components/link';
import OtpVerificationModal from '@/components/otp-verification-modal';
import type {AuthStepEventData} from '@/components/otp-verification-modal';
import {PopularPaysLogoFull} from '@/components/unusual-icons';
import useTrackAuthStepEvent from '@/hooks/analytics/use-track-auth-step-event';
import useIsMobile from '@/hooks/use-is-mobile';
import useLtxToken from '@/hooks/use-ltx-token';
import useNavigation from '@/hooks/use-navigation';
import {DecodedToken} from '@/hooks/use-revalidate-session/useRevalidateSession';
import useToken from '@/hooks/use-token';
import styles from './BaseAuth.module.scss';
import OauthSection, {type OauthOptions} from './OauthSection';

export type ButtonAppearance = ButtonProps['appearance'];

export type BaseAuthProps = {
  testID?: string;
  mode: AuthMode;
  product: Product;
  title: string;
  subtitle?: string;
  footer?: string | React.ReactNode;
  redirectTo?: string;
  isMarketingExperiment?: boolean;
  initialEmail?: string;
  emailLabelKey?: string;
  submitButtonTextKey?: string;
  utm_campaign?: string;
  queryParamsWithoutUtmCampaign?: {readonly [x: string]: string | undefined};
  mainButtonAppearance?: ButtonAppearance;
  onlyEmail?: boolean;
  oauthOptions?: OauthOptions;
  requireTermsAgreement?: boolean;
  mediaComponent?: React.ReactNode | null;
  loginLinkText?: string;
  loginLinkButton?: string;
  signupLinkText?: string;
  signupLinkButton?: string;
  onAuthSuccess?: () => void;
};

export type Form = {
  email: string;
  brandName?: string;
  firstName?: string;
  lastName?: string;
  isTermsChecked?: boolean;
  requestedPlanId?: string;
  isTestBrandChecked?: boolean;
  mainButtonAppearance?: ButtonAppearance;
};

const AUTH_LINK_ROUTES = {
  signup: {
    'content-lab': ROUTES.LOGIN_CONTENT_LAB,
    'campaign-management': ROUTES.LOGIN,
  },
  login: {
    'content-lab': ROUTES.SIGN_UP_CONTENT_LAB,
    'campaign-management': ROUTES.SIGN_UP,
  },
};
// Turnstile tokens expire after ~5 minutes, refresh proactively at 4 minutes
const TOKEN_REFRESH_THRESHOLD_MS = 4 * 60 * 1000; // 4 minutes
const SIGNUP_MOBILE_WIDTH = 1100;

/**
 * Extracts the error code from Turnstile error string
 * Formats: "600010" or "600010: The generic_challenge_failure"
 * @param error - The error string from Turnstile
 * @returns The error code (e.g., "600010") or null if not found
 */
const extractTurnstileErrorCode = (error: string): string | null => {
  // Match either plain number or number followed by colon and description
  const match = error.match(/^(\d+)(?::|$)/);
  return match ? match[1] : null;
};

/**
 * Checks if an error code indicates bot detection
 * Error codes 300*** and 600*** indicate bot detection or unusual visitor behavior
 * @param errorCode - The error code to check
 * @returns true if the error code indicates bot detection
 */
const isBotDetectionError = (errorCode: string): boolean => {
  return errorCode.startsWith('300') || errorCode.startsWith('600');
};

const contentLabIcon = '/assets/svg/icon-content-lab.svg';
const contentLabLogo = '/assets/svg/logo-content-lab.svg';

function ContentLabLogo() {
  return (
    <Box display="flex" alignItems="center" gap="8px">
      <img src={contentLabLogo} alt="Ltx Scaler Logo" />
      <img src={contentLabIcon} alt="Ltx Scaler Icon" />
    </Box>
  );
}

const MARKETING_EXPERIMENT_GOOGLE_CAMPAIGN_IDS = [
  20350428894, 20601153252, 20607237560,
];

const MARKETING_EXPERIMENT_SUBSCRIPTION_URL =
  'https://payments.popularpays.com/purchase/v1/popularpays_super/after_signup/how-trial-works';

const AUTH_MAPPINGS = {
  signup: {
    flowName: flowNames.AUTH.NEW_SIGNUP,
    screenName: screenNames.AUTH.NEW_SIGNUP,
    provider: providers.SIGNUP,
  },
  login: {
    flowName: flowNames.AUTH.LOGIN,
    screenName: screenNames.LOGIN.MAIN_LOGIN_SCREEN,
    provider: providers.FORTRESS,
  },
} as const;

function BaseAuth(props: BaseAuthProps) {
  const {
    testID = 'auth',
    mode,
    product,
    title,
    subtitle,
    footer,
    isMarketingExperiment,
    initialEmail,
    emailLabelKey,
    submitButtonTextKey,
    utm_campaign,
    queryParamsWithoutUtmCampaign,
    redirectTo,
    mainButtonAppearance = 'neutral',
    onlyEmail = false,
    oauthOptions,
    requireTermsAgreement = false,
    mediaComponent,
    loginLinkText,
    loginLinkButton,
    signupLinkText,
    signupLinkButton,
    onAuthSuccess,
  } = props;
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedPlanId =
    new URLSearchParams(location.search).get('rpid') || '';
  const [enableAutoRedirectToHP, setEnableAutoRedirectToHP] = useState(true);
  const [form, setForm] = useState<Form>({
    email: initialEmail || '',
    firstName: '',
    lastName: '',
    brandName: '',
    isTermsChecked: false,
    requestedPlanId,
    isTestBrandChecked: false,
  });
  const [isSubmittingLoading, setIsSubmittingLoading] = useState(false);
  const [error, setError] = useState({
    message: '',
    title: '',
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string>('');
  const [captchaTokenTimestamp, setCaptchaTokenTimestamp] = useState<
    number | null
  >(null);
  const navigation = useNavigation();
  const {setLtxToken} = useLtxToken();
  const {setToken} = useToken();
  const isTablet = useIsMobile(SIGNUP_MOBILE_WIDTH);
  const isMobile = useIsMobile();
  const {isAuthenticated} = useToken(true);
  const {ended: trackAuthStepEnded} = useTrackAuthStepEvent(mode);
  const turnstileRef = useRef<TurnstileInstance>(null);
  const authConfig = AUTH_MAPPINGS[mode];

  const fortressFlowName =
    product === 'content-lab'
      ? FORTRESS_FLOWS.ltxScaler
      : FORTRESS_FLOWS.popularPays;

  useEffect(() => {
    const flow = AnalyticsService.createFlow(authConfig.flowName);
    AnalyticsService.startFlow(flow.flow_name);
    AnalyticsService.screenPresented(authConfig.screenName, '', {
      trigger_flow_id: flow.flow_id,
      trigger_flow_name: flow.flow_name,
    });
  }, []);

  // Check for error query parameter on component mount
  useEffect(() => {
    const errorParam = searchParams.get('error');
    if (errorParam) {
      setError({
        title: translate('views.auth.signup.general-error'),
        message: errorParam,
      });
    }
  }, [searchParams]);

  useEffect(() => {
    if (isAuthenticated && enableAutoRedirectToHP) {
      navigation.navigate(redirectTo || '/');
    }
  }, [isAuthenticated, enableAutoRedirectToHP, redirectTo]);

  useEffect(() => {
    if (isMarketingExperiment) {
      setEnableAutoRedirectToHP(false);
    }
  }, [isMarketingExperiment]);

  // Unified handler for login/signup
  const onAuth = async (
    verifySession: VerifyOTPAndSaveTokenCookiesResponse,
    stepEventData: AuthStepEventData
  ) => {
    setError({title: '', message: ''});
    setIsSubmittingLoading(true);

    try {
      // Call the dashboard user sign in endpoint with verifySession data
      const authResponse =
        await authenticationUpdaters.authenticateDashboardUser({
          verifySession,
          firstName: form.firstName,
          lastName: form.lastName,
          organizationName: form.brandName,
          product,
        });
      trackAuthStepEnded({
        ...stepEventData,
        reason: reasons.AUTH.SUCCESS,
        error: null,
      });
      AnalyticsService.screenDismissed(authConfig.screenName, '');
      AnalyticsService.endFlow(authConfig.flowName, {
        result: resultFlows.GENERIC.SUCCESS,
      });
      await AnalyticsService.dischargeEventBatch();
      const decodedToken = parseJwt(authResponse.data.token) as DecodedToken;
      await setToken(decodedToken.token);
      await setLtxToken(verifySession.token);
      const flowEvent = AnalyticsService.getFlow(authConfig.flowName) || {};
      trackAuthStepEnded({
        flow_id: flowEvent.flow_id || '',
        process_id: '',
        provider: authConfig.provider,
        request_type: '',
        input_value: '',
        reason: reasons.AUTH.SUCCESS,
        error: '',
      });
      if (isMarketingExperiment && mode === 'signup') {
        const restParamsStr = Object.entries(
          queryParamsWithoutUtmCampaign || {}
        )
          .map(([key, value]) => {
            if (key === '*') return '';
            return `&${key}=${value}`;
          })
          .join('');
        navigation.navigate(
          `${MARKETING_EXPERIMENT_SUBSCRIPTION_URL}?utm_campaign=${utm_campaign}&utm_wpflow=true${restParamsStr}`,
          null,
          false
        );
      } else {
        navigation.navigate(redirectTo || '/');
      }
      if (mode === 'signup') {
        await signupPixelEvents();
      }
      onAuthSuccess?.();
    } catch (e: any) {
      const errorMessage =
        e?.errors?.map((err: any) => err?.detail || '').join(', ') ||
        e?.message ||
        translate('views.auth.signup.general-error-message');
      const errorTitle = translate('views.auth.signup.general-error');
      setError({title: errorTitle, message: errorMessage});
      setIsModalOpen(false);
      setIsSubmittingLoading(false);
      trackAuthStepEnded({
        ...stepEventData,
        reason: reasons.AUTH.FAILURE,
        error: errorMessage,
      });
      Sentry.captureException(e, {
        title: `Dashboard User ${
          mode === 'signup' ? 'Sign Up' : 'Login'
        } Error`,
        email: form.email,
        errorMessage,
      });
      refreshCaptchaToken(true);
    }
  };

  const onNavigateToOther = () => {
    const otherAuthConfig =
      AUTH_MAPPINGS[mode === 'signup' ? 'login' : 'signup'];
    AnalyticsService.screenDismissed(
      authConfig.screenName,
      otherAuthConfig.screenName
    );
    AnalyticsService.endFlow(authConfig.flowName, {
      result: resultFlows.GENERIC.CANCELLED,
    });
  };

  const handleClearError = () => {
    setError({title: '', message: ''});
    // Remove the error parameter from URL query params
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('error');
    setSearchParams(newSearchParams);
  };

  // Helper to get the correct route for login/signup link while keeping the existing query params
  function getAuthLinkRoute() {
    return `${AUTH_LINK_ROUTES[mode]?.[product]}${location.search ? `${location.search}` : ''}`;
  }

  const turnstileSiteKey = getEnv().VITE_TURNSTILE_SITE_KEY || '';

  const refreshCaptchaToken = async (
    forceRefresh = false
  ): Promise<string | null> => {
    try {
      // Check if token needs refresh (older than 4 minutes or forced)
      const now = Date.now();
      const shouldRefresh =
        forceRefresh ||
        !captchaToken ||
        !captchaTokenTimestamp ||
        now - captchaTokenTimestamp > TOKEN_REFRESH_THRESHOLD_MS;

      if (!shouldRefresh && captchaToken) {
        // Token is still valid, return existing token
        return captchaToken;
      }

      // Reset the turnstile to get a new token
      turnstileRef.current?.reset?.();
      // Request the new token
      const newCaptchaToken =
        await turnstileRef.current?.getResponsePromise?.();
      if (newCaptchaToken) {
        setCaptchaToken(newCaptchaToken);
        setCaptchaTokenTimestamp(now);
        return newCaptchaToken;
      }
    } catch (e: any) {
      Sentry.captureException(e);
    }
    return null;
  };

  const handleTurnstileSuccess = useCallback((token: string) => {
    setCaptchaToken(token);
    setCaptchaTokenTimestamp(Date.now());
  }, []);

  const handleTurnstileError = useCallback(
    (_error?: string) => {
      setCaptchaToken('');
      setCaptchaTokenTimestamp(null);

      // Check for bot detection error codes (300*** and 600***)
      // https://developers.cloudflare.com/turnstile/troubleshooting/client-side-errors/error-codes/
      if (_error) {
        const errorCode = extractTurnstileErrorCode(_error);
        if (errorCode && isBotDetectionError(errorCode)) {
          const flowEvent = AnalyticsService.getFlow(authConfig.flowName) || {};
          trackAuthStepEnded({
            flow_id: flowEvent.flow_id || '',
            process_id: '',
            provider: authConfig.provider,
            request_type: 'request_turnstile_captcha_token',
            input_value: errorCode,
            reason: reasons.AUTH.FAILURE,
            error: 'turnstile_captcha_challenge_failed',
          });
          Sentry.captureMessage('Turnstile captcha challenge failed', {
            level: 'warning',
            tags: {
              event: 'turnstile_captcha_challenge_failed',
              mode,
              errorCode,
            },
            extra: {
              errorCode,
              errorMessage: _error,
              mode,
              userAgent: navigator.userAgent,
            },
          });
        }
      }
    },
    [authConfig.flowName, authConfig.provider, mode]
  );

  const handleTurnstileExpire = useCallback(() => {
    setCaptchaToken('');
    setCaptchaTokenTimestamp(null);
  }, []);

  return (
    <div className={styles.container} data-testid={testID}>
      <OtpVerificationModal
        isOpen={isModalOpen}
        email={form.email}
        setIsModalOpen={setIsModalOpen}
        authenticateCallback={onAuth}
        mainButtonAppearance={mainButtonAppearance}
        mode={mode}
        product={product}
        fortressFlowName={fortressFlowName}
        captchaToken={captchaToken}
        refreshCaptchaToken={refreshCaptchaToken}
      />
      {isTablet || !mediaComponent ? (
        <div className={styles.fullHeightDummy} />
      ) : (
        <div className={styles.halfScreenImageContainer}>{mediaComponent}</div>
      )}
      <div className={styles.formContainer}>
        {error.message && (
          <Banner
            isOpen
            severity="error"
            title={error.title}
            onActionClick={handleClearError}
          >
            {error.message}
          </Banner>
        )}
        <div
          className={clsx(
            styles.formBox,
            onlyEmail && !isMobile && styles.narrowForm
          )}
        >
          {isTablet && (
            <div>
              {product === 'campaign-management' ? (
                <PopularPaysLogoFull />
              ) : (
                <ContentLabLogo />
              )}
            </div>
          )}
          <div className={styles.header}>
            <Headline
              size={isMobile ? 'lg' : 'xl'}
              className={styles.formTitle}
              whiteSpace="pre-line"
            >
              {title}
            </Headline>
            <Body
              size={isMobile ? 'md' : 'xl'}
              color={designSystemToken('semantic.fg.secondary')}
            >
              {subtitle}
            </Body>
          </div>
          <OauthSection
            oauthOptions={oauthOptions}
            product={product}
            setError={setError}
            mode={mode}
          />
          <AuthForm
            form={form}
            setForm={setForm}
            setIsModalOpen={setIsModalOpen}
            isLoading={isSubmittingLoading}
            setIsLoading={setIsSubmittingLoading}
            setGeneralError={setError}
            emailLabelKey={emailLabelKey}
            isMobile={isTablet}
            disableEmail={!!initialEmail}
            submitButtonAppearance={mainButtonAppearance}
            onlyEmail={onlyEmail}
            requireTermsAgreement={requireTermsAgreement}
            submitButtonTextKey={submitButtonTextKey}
            mode={mode}
            fortressFlowName={fortressFlowName}
            captchaToken={captchaToken}
            refreshCaptchaToken={refreshCaptchaToken}
          />
          {loginLinkText ||
          signupLinkText ||
          loginLinkButton ||
          signupLinkButton ? (
            <div className={styles.loginLink}>
              {loginLinkText || signupLinkText ? (
                <Body
                  size="sm"
                  color={designSystemToken('semantic.fg.secondary')}
                >
                  {mode === 'signup' ? loginLinkText : signupLinkText}
                </Body>
              ) : null}
              {loginLinkButton || signupLinkButton ? (
                <Link
                  to={getAuthLinkRoute()}
                  onClick={onNavigateToOther}
                  keepBrandIdIfPresent={false}
                >
                  <Label
                    size="sm"
                    color={designSystemToken(
                      `semantic.fg.${mainButtonAppearance}`
                    )}
                  >
                    {mode === 'signup' ? loginLinkButton : signupLinkButton}
                  </Label>
                </Link>
              ) : null}
            </div>
          ) : null}
          {typeof footer === 'string' ? (
            <Body
              className={styles.termsFootnote}
              size="sm"
              color={designSystemToken('semantic.fg.secondary')}
            >
              {footer}
            </Body>
          ) : (
            footer
          )}
        </div>
      </div>
      <Turnstile
        ref={turnstileRef}
        siteKey={turnstileSiteKey}
        onSuccess={handleTurnstileSuccess}
        onError={handleTurnstileError}
        onExpire={handleTurnstileExpire}
        options={{
          size: 'invisible',
        }}
      />
    </div>
  );
}

export default BaseAuth;
